// USD amount + payment-request-link helpers.
//
// The wallet asset is USDC (a USD stablecoin, ~1:1). We present it to users as
// "USD" — a normal user thinks in dollars and needn't care it's USDC. Amounts
// use raw digits + dot decimal in URLs (locale-proof: EU writes 12.740,33), and
// are shown grouped with a $ on screen. Ticker is a three-letter code; only USD
// is supported for now (GBP/EUR imply FX — a later feature).

import { formatCode } from "./codes";

export const MAX_AMOUNT = 1_000_000;
export const TICKER = "USD";

// v0.80.29 — the link grammar is {CCY}{amount}: `USD25.00`, `EUR22.33`. USD
// stays the default so every existing link keeps working unchanged.
export const TICKERS = ["USD", "EUR", "GBP"] as const; // 1.4.26 — GBP7.00 links

/** "$12,740.33" — for display. */
export function formatUsd(n: number): string {
  return `$${groupAmount(n)}`;
}

/**
 * The everyday amount (v0.70.5 global rule): "$1.00" … "$9.00" keep their
 * cents so a single digit doesn't look like a typo; from $10 a whole amount
 * drops the ".00" — "$10", "$12.50", "$250".
 */
export function formatUsdShort(n: number): string {
  const v = Number.isFinite(n) ? n : 0;
  if (v < 10) return formatUsd(v);
  return formatUsd(v).replace(/\.00$/, "");
}

/** "12 Sep 26" — the statement date format (v0.70.5). */
export function formatDayShort(ms: number): string {
  const d = new Date(ms);
  const mon = d.toLocaleString("en-GB", { month: "short" });
  return `${String(d.getDate()).padStart(2, "0")} ${mon} ${String(d.getFullYear()).slice(-2)}`;
}
/** "12 Sep" — for the Balance panel (v0.70.16). */
export function formatDayNoYear(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getDate()).padStart(2, "0")} ${d.toLocaleString("en-GB", { month: "short" })}`;
}
/** Same, from an SQLite "YYYY-MM-DD HH:MM:SS" (UTC) string. */
export function formatDayFromSql(s: string | null | undefined): string {
  if (!s) return "";
  const ms = Date.parse(s.replace(" ", "T") + "Z");
  return Number.isFinite(ms) ? formatDayShort(ms) : "";
}

/** "12,740.33" — grouped, 2 dp, no symbol. */
export function groupAmount(n: number): string {
  return (Number.isFinite(n) ? n : 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** "12740.33" — canonical URL / transfer form: 2 dp, dot decimal, no grouping. */
export function canonicalAmount(n: number): string {
  return (Number.isFinite(n) ? n : 0).toFixed(2);
}

/**
 * Parse a "{CCY}{amount}" path segment → { amount, currency }, or null if it
 * isn't valid. Case-insensitive; a supported ticker, positive, ≤ MAX_AMOUNT,
 * at most 2 decimals. v0.80.29 — any listed currency, not just USD.
 */
export function parseAmountSeg(seg: string): { amount: number; currency: string } | null {
  const m = /^([a-z]{3})(\d+(?:\.\d{1,2})?)$/i.exec(seg.trim());
  if (!m) return null;
  const ccy = m[1].toUpperCase();
  if (!(TICKERS as readonly string[]).includes(ccy)) return null;
  const n = Number(m[2]);
  if (!(n > 0) || n > MAX_AMOUNT) return null;
  return { amount: n, currency: ccy };
}

/** Back-compat: the amount only (callers that still assume dollars). */
export function parseAmountSegment(seg: string): number | null {
  return parseAmountSeg(seg)?.amount ?? null;
}

/** Reference → URL-safe slug: spaces→hyphens, drop unsafe chars, ≤40. */
export function slugifyRef(ref: string): string {
  return ref
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\p{L}\p{N}-]+/gu, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40)
    .replace(/-$/, "");
}

/** Reference slug → display: hyphens back to spaces. */
export function deslugifyRef(slug: string): string {
  return slug.replace(/-+/g, " ").trim();
}

/**
 * Title Case a wallet display name: "crackly snipe 7926" → "Crackly Snipe 7926".
 * Names are generated/stored lowercase; this is applied at every render point so
 * both new (title-cased at source) and existing (lowercase) rows display right.
 * The 4-digit number is untouched. Do NOT apply to references (user text).
 */
export function titleCase(s: string): string {
  return s
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

/** Build the request path: /{CODE}/USD{amount}[/{ref}] — the code is shown in
 *  its grouped/cased display form ("7A82-ER7Q"); the resolver canonicalises. */
export function requestPath(
  nameSlug: string,
  amount: number,
  ref?: string,
  ccy: string = TICKER,   // v0.80.29 — {CCY}{amount}; USD by default
): string {
  const code = formatCode(nameSlug);
  const base = `/${code}/${ccy.toUpperCase()}${canonicalAmount(amount)}`;
  const r = ref ? slugifyRef(ref) : "";
  return r ? `${base}/${r}` : base;
}

/** Full absolute request URL for sharing. */
export function requestUrl(
  origin: string,
  nameSlug: string,
  amount: number,
  ref?: string,
  ccy: string = TICKER,
): string {
  return `${origin.replace(/\/$/, "")}${requestPath(nameSlug, amount, ref, ccy)}`;
}
